import IconeAjuda from '@/global/components/IconeAjuda'
import Menu from '@/global/components/MenuV2'
import { Box, Divider, Grid } from '@mui/material'
import { FormField, FormFieldCheckbox } from 'global/components/Form'
import { Link } from 'global/components/Link'
import { hasPermission } from 'hooks/useBackendMenu'
import { ExternalLinkIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { regrasCalculo, tabelaHorarios } from 'service/api'
import { useStore } from 'store'
import AutocompleteFuncao from './AutocompleteFuncao'

export function ParametrosDoCalculo({ editar }: { editar?: boolean }) {
    const { empresaId, id } = useParams()
    const cliente = useStore(state => state.customer)
    const empresaIdStore = useStore(state => state.companyId)
    const { watch } = useFormContext()
    const tabelaHorarioId = watch('tabelaHorarioId')

    const { data: tabelaHorariosData } = tabelaHorarios.useFetch({
        queryParams: id ? { clienteId: id } : {},
        enabled: !!id || !!cliente,
    })

    const { data: rgCalculoData } = regrasCalculo.useFetch({
        queryParams: id ? { clienteId: id } : {},
        enabled: !!empresaId || !!empresaIdStore,
    })

    return (
        <Box component="section" id="parametros">
            <Divider sx={{ marginY: 2 }} textAlign="center">
                Parâmetros do cálculo
            </Divider>
            <Grid
                container
                columns={24}
                spacing={1}
                columnSpacing={2}
                alignItems="self-start"
            >
                <Grid item xs={8}>
                    <FormField
                        name="matricula"
                        label="Matrícula"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ maxlength: 10 }}
                        placeholder={
                            editar ? undefined : '(Gerar automaticamente)'
                        }
                        required={editar}
                        type="number"
                        sx={{
                            'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button':
                                {
                                    '-webkit-appearance': 'none',
                                    margin: 0,
                                },
                            'input[type=number]': {
                                '-moz-appearance': 'textfield',
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={8}>
                    <FormField
                        name="dtAdmissao"
                        InputLabelProps={{ shrink: true }}
                        label="Data de admissão"
                        type="date"
                        required
                    />
                </Grid>
                <Grid item xs={8}>
                    <FormField
                        name="dtRescisao"
                        InputLabelProps={{ shrink: true }}
                        label="Data de rescisão"
                        type="date"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        required
                        name="regraCalculo"
                        label="Regra de cálculo"
                        select
                        onKeyDownCapture={e => e.stopPropagation()}
                    >
                        {hasPermission(['create', 'RegrasDeCálculo'], () => (
                            <AdicionarNova href="/cadastros/regras-calculo/adicionar" />
                        ))}
                        {rgCalculoData?.map(regra => (
                            <Menu.Item key={regra.id} value={+regra.id}>
                                {regra.nome}
                            </Menu.Item>
                        ))}
                    </FormField>
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        name="tabelaHorarioId"
                        label={'Tabela de horário'}
                        select
                        onKeyDownCapture={e => e.stopPropagation()}
                        {...(editar && {
                            disabled: true,
                            label: tabelaHorarioId ? (
                                <Link
                                    href={`/cadastros/tabela-horarios/${tabelaHorarioId}/editar`}
                                    className="inline-flex items-center gap-2"
                                >
                                    Tabela de horário atual{' '}
                                    <ExternalLinkIcon className="size-4" />
                                </Link>
                            ) : (
                                'Tabela de horário'
                            ),
                        })}
                    >
                        {hasPermission(['create', 'TabelaDeHorários'], () => (
                            <AdicionarNova href="/cadastros/tabela-horarios/adicionar" />
                        ))}
                        <Menu.Item value="">Nenhuma (Horista)</Menu.Item>
                        {tabelaHorariosData?.map((tabelaHorario, index) => (
                            <Menu.Item key={index} value={+tabelaHorario.id}>
                                {tabelaHorario.nome}
                            </Menu.Item>
                        ))}
                    </FormField>
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        name="inicioBancoHoras"
                        label="Início do banco de horas"
                        type="month"
                        disabled // TODO: reabilitar quando implementado
                        InputProps={{
                            endAdornment: (
                                <IconeAjuda>
                                    <strong>
                                        Temporariamente desabilitado para
                                        manutenção
                                    </strong>
                                    <br />
                                    <br />O início do banco de horas é a
                                    competência a partir da qual o funcionário
                                    começará a acumular horas extras ou faltas.
                                    <br />
                                    Deixe em branco caso o funcionário sempre
                                    utilizou ou nunca utilizará o banco de
                                    horas.
                                </IconeAjuda>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <AutocompleteFuncao />
                </Grid>
                <Grid item xs={9} mt={-1}>
                    <FormFieldCheckbox
                        name="horarioNoturno"
                        label="Horário noturno"
                    />
                </Grid>
                <Grid item xs={9} mt={-1}>
                    <FormFieldCheckbox
                        name="desconsiderarFeriados"
                        label="Desconsiderar feriados"
                    />
                </Grid>
            </Grid>
            <div className="h-16" />
        </Box>
    )
}

function AdicionarNova({ href }: { href: string }) {
    const { watch } = useFormContext()
    const navigate = useNavigate()

    return (
        <Menu.Item
            value="0"
            onClick={() => {
                localStorage.setItem(
                    '@rascunho:funcionario',
                    JSON.stringify(watch()),
                )
                navigate(href)
            }}
        >
            Adicionar Nova
        </Menu.Item>
    )
}
